import type React from 'react'
import { useEffect, useState } from 'react'
import { cn } from '@/utils'
import { useActiveSelection } from '@/components/app/editor/active-object-context'
import { useColorsQuery } from '@/api/hooks/use-colors-query'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { WireColor } from '@/api/types/color'
import { resolvePaletteFill } from '@/lib/colors/resolve-palette-color'
import { useColorPalette } from '@/contexts/color-palette-context'
import {
  SHAPE_DEFAULT_FILL_COLOR_ID,
  SHAPE_DEFAULT_FILL_TINT,
} from '@/lib/elements/constants/shape-default-fill-color-id'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeStyleOverrides } from '@/lib/shapes/types/shape-style-overrides'
import type { OnBatchShapesCommitted } from '@/lib/transforms/types/on-batch-shapes-committed'
import {
  COLOR_LIST_DEFAULT_HEIGHT,
  ColorPopover,
  DRAGGABLE_POPOVER_ATTR,
  useColorSwatchDrag,
  usePopoverDrag,
} from '../color-popover'
import { ColorIndicator } from './color-indicator'
import { NONE_COLOR, fillPaintForColor, isNoneColor } from '@/lib/colors/fill-paint'
import { useFillColorApply } from './hooks/use-fill-color-apply'
import { getMenuBarBottom } from '@/lib/editor/floating-canvas-chrome-bounds'

type ColorTarget = 'text' | 'fill' | 'stroke'

interface ColorEntryProps {
  shapeManagerRef?: React.RefObject<ShapeManagerRef | null>
  onBatchShapesCommitted?: OnBatchShapesCommitted
}

export function ColorEntry({ shapeManagerRef, onBatchShapesCommitted }: ColorEntryProps) {
  const { activeObject } = useActiveSelection()
  const { data: allColors = [] } = useColorsQuery()
  const palette = useColorPalette()
  const { applyFillColor, previewFillTint, commitFillTint, selectionTint, isTintMixed } =
    useFillColorApply({
      shapeManagerRef,
      onBatchShapesCommitted,
      palette,
    })
  const [openTarget, setOpenTargetState] = useState<ColorTarget | null>(null)
  const [listHeight, setListHeight] = useState(COLOR_LIST_DEFAULT_HEIGHT)
  // Read when a popover opens: Radix may shift a tall popover up, but never above the menu bar.
  const [menuBarBottom, setMenuBarBottom] = useState(0)

  // The menu bar changes height at the `lg` breakpoint, so follow window resizes while open.
  useEffect(() => {
    if (openTarget === null) return

    const update = () => setMenuBarBottom(getMenuBarBottom())

    window.addEventListener('resize', update)

    return () => window.removeEventListener('resize', update)
  }, [openTarget])
  const {
    offset,
    isDragging,
    hasMoved,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    resetDrag,
  } = usePopoverDrag()

  function setOpenTarget(next: ColorTarget | null) {
    if (next !== openTarget) resetDrag()
    if (next !== null) setMenuBarBottom(getMenuBarBottom())
    // The list height carries across Text, Fill and Stroke and resets only when the popover closes.
    if (next === null) setListHeight(COLOR_LIST_DEFAULT_HEIGHT)
    setOpenTargetState(next)
  }

  // Shared by the three PopoverContents. Outside clicks never close the popover, so a shape can be
  // selected and recolored with it open; its close button or rail button closes it. Once dragged,
  // Escape doesn't close it either, like the Photo Effects popover.
  const contentProps = {
    side: 'right' as const,
    sideOffset: 8,
    align: 'start' as const,
    collisionPadding: { top: menuBarBottom },
    className: 'p-0 w-auto',
    [DRAGGABLE_POPOVER_ATTR]: '',
    style: { translate: `${offset.x}px ${offset.y}px` },
    onInteractOutside: (e: Event) => e.preventDefault(),
    // Switching targets closes this popover after the next one has opened; returning focus to this
    // trigger would read as a focus-outside on the new popover and close it straight away.
    onCloseAutoFocus: (e: Event) => e.preventDefault(),
    onEscapeKeyDown: (e: KeyboardEvent) => {
      if (hasMoved) e.preventDefault()
    },
  }
  const dragProps = {
    listHeight,
    onListHeightChange: setListHeight,
    isDragging,
    onDragStart: handleDragStart,
    onDragMove: handleDragMove,
    onDragEnd: handleDragEnd,
  }

  // Local override when the user picks a text color from the popover.
  const [pickedTextColor, setPickedTextColor] = useState<string | null>(null)
  // Local override when the user picks a fill color from the popover.
  const [pickedFillColor, setPickedFillColor] = useState<string | null>(null)
  // Local override when the user picks a stroke color from the popover.
  const [pickedStrokeColor, setPickedStrokeColor] = useState<string | null>(null)
  // Snapshot of the manager's last-used fill styles, kept in state so tint-slider drags re-render
  // even when nothing is selected and pickedFillColor is still null (§5: never read a ref at render
  // time for values that must stay fresh). Initialized from the manager's own defaults so the Fill
  // button shows a draggable color even before the first pick.
  const [lastUsedFillState, setLastUsedFillState] = useState<ShapeStyleOverrides | null>({
    fillColorId: SHAPE_DEFAULT_FILL_COLOR_ID,
    fillTint: SHAPE_DEFAULT_FILL_TINT,
  })

  const fillColor =
    resolvePaletteFill(palette, activeObject?.fillColorId, activeObject?.fillTint) ??
    activeObject?.fill ??
    null
  const strokeColor = activeObject?.stroke ?? null

  // Derive text color from the first run of the first paragraph on the active shape.
  // colorId (number) → hexValue via the catalog.
  const activeTextColorId =
    activeObject?.paragraphs?.[0]?.textLines?.[0]?.textRuns?.[0]?.fillStyle?.colorId ?? null
  const derivedTextColor =
    activeTextColorId != null
      ? (allColors.find((c) => c.colorId === activeTextColorId)?.hexValue ?? null)
      : null

  // Read fillColorId/fillTint directly from the active shape when one is selected.
  const activeFillColorId = activeObject?.fillColorId ?? null
  const activeFillTint = activeObject?.fillTint ?? 100

  // Single color source for the fill drag: selected shape's fill when one is selected, otherwise
  // the manager's last-used fill. Both the ghost preview and the drop use the same WireColor, so
  // they can never show one color and apply another. A fill with no palette color is None, and
  // drags as None.
  const fillDragColorId =
    activeObject != null ? activeFillColorId : (lastUsedFillState?.fillColorId ?? null)
  const fillDragTint = activeObject != null ? activeFillTint : (lastUsedFillState?.fillTint ?? 100)
  const fillDragWireColor: WireColor | null =
    fillDragColorId != null ? (palette?.get(fillDragColorId) ?? null) : NONE_COLOR
  const fillSwatchDrag = useColorSwatchDrag(fillDragWireColor, fillDragTint)

  // Reverse-lookup hex → colorId for stroke (no durable colorId on ShapeData for stroke yet).
  const activeStrokeColorId =
    strokeColor != null
      ? (allColors.find((c) => c.hexValue === strokeColor)?.colorId ?? null)
      : null

  // Popover pick wins over the derived value; null falls back to foreground in ColorIndicator.
  const textColor = pickedTextColor ?? derivedTextColor
  // When a shape is selected its fill is always authoritative. The last-picked color is only used
  // as a fallback when nothing is selected (so the indicator shows the last-used fill for new shapes).
  const displayFillColor = activeObject != null ? fillColor : (pickedFillColor ?? fillColor)
  const displayStrokeColor = pickedStrokeColor ?? strokeColor

  function handleTextColorSelect(color: WireColor) {
    setPickedTextColor(isNoneColor(color) ? null : color.hexValue)
  }

  function handleFillColorSelect(color: WireColor, tint: number) {
    setPickedFillColor(isNoneColor(color) ? null : (fillPaintForColor(color, tint).fill ?? null))
    applyFillColor(color, tint)
    setLastUsedFillState(shapeManagerRef?.current?.getLastUsedStyles() ?? null)
  }

  function handleFillTintChange(tint: number) {
    previewFillTint(tint)
    const styles = shapeManagerRef?.current?.getLastUsedStyles() ?? null

    setLastUsedFillState(styles)
    if (styles?.fill) setPickedFillColor(styles.fill)
  }

  function handleStrokeColorSelect(color: WireColor) {
    setPickedStrokeColor(isNoneColor(color) ? null : color.hexValue)
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="w-6 h-px bg-border shrink-0" />

      <Popover
        open={openTarget === 'text'}
        onOpenChange={(open) => setOpenTarget(open ? 'text' : null)}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Text color"
            className={cn(
              'flex items-center justify-center w-8 h-8 rounded-md',
              openTarget === 'text' && 'bg-muted'
            )}
          >
            <ColorIndicator kind="text" color={textColor} />
          </button>
        </PopoverTrigger>
        <PopoverContent {...contentProps}>
          <ColorPopover
            target="text"
            {...dragProps}
            activeColorId={activeTextColorId}
            onClose={() => setOpenTarget(null)}
            onColorSelect={(color) => handleTextColorSelect(color)}
          />
        </PopoverContent>
      </Popover>

      <Popover
        open={openTarget === 'fill'}
        onOpenChange={(open) => setOpenTarget(open ? 'fill' : null)}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Fill color"
            className={cn(
              'flex items-center justify-center w-8 h-8 rounded-md',
              openTarget === 'fill' && 'bg-muted'
            )}
            onPointerDown={fillSwatchDrag.onPointerDown}
            onPointerMove={fillSwatchDrag.onPointerMove}
            onPointerUp={fillSwatchDrag.onPointerUp}
            onPointerCancel={fillSwatchDrag.onPointerCancel}
          >
            <ColorIndicator kind="fill" color={displayFillColor} />
          </button>
        </PopoverTrigger>
        <PopoverContent {...contentProps}>
          <ColorPopover
            target="fill"
            {...dragProps}
            activeColorId={activeFillColorId}
            initialTint={selectionTint ?? activeFillTint}
            isTintMixed={isTintMixed}
            onClose={() => setOpenTarget(null)}
            onColorSelect={(color, tint) => handleFillColorSelect(color, tint)}
            onTintChange={handleFillTintChange}
            onTintCommit={commitFillTint}
          />
        </PopoverContent>
      </Popover>

      <Popover
        open={openTarget === 'stroke'}
        onOpenChange={(open) => setOpenTarget(open ? 'stroke' : null)}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Stroke color"
            className={cn(
              'flex items-center justify-center w-8 h-8 rounded-md',
              openTarget === 'stroke' && 'bg-muted'
            )}
          >
            <ColorIndicator kind="stroke" color={displayStrokeColor} />
          </button>
        </PopoverTrigger>
        <PopoverContent {...contentProps}>
          <ColorPopover
            target="stroke"
            {...dragProps}
            activeColorId={activeStrokeColorId}
            onClose={() => setOpenTarget(null)}
            onColorSelect={(color) => handleStrokeColorSelect(color)}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
